import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import pmmService from '../services/paymentMigrationManagerService';
import paymentporterService from '../services/paymentporterService';
import entityMktSettingsService from '../services/partnerMktSettingsService';
import hotelService from '../services/hotelService';
import useCredentialStep from './useCredentialStep';
import WizardStepper from './WizardStepper';
import WizardHotelStep from './WizardHotelStep';
import WizardPmsStep from './WizardPmsStep';
import WizardCredentialsStep from './WizardCredentialsStep';
import WizardMarkerStep from './WizardMarkerStep';
import './AddPaymentPorterWizard.css';

const today = new Date();
const DEFAULT_START = today.toISOString().split('T')[0];
const DEFAULT_END = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

const STEPS = [
    { num: 1, label: 'Name' },
    { num: 2, label: 'Hotel' },
    { num: 3, label: 'PMS' },
    { num: 4, label: 'Credentials' },
    { num: 5, label: 'Marker' },
    { num: 6, label: 'Dates' },
];

const AddPaymentMigrationManagerWizard = () => {
    const navigate = useNavigate();
    const { selectedAccount, selectedPartner, isChainAdmin } = useAccount();
    const { isSuperAdmin, isPartner, solePartnerId } = useAuth();

    const [step, setStep] = useState(1);
    const [form, setForm] = useState({ name: '', description: '' });
    const [errors, setErrors] = useState({});

    const [hotels, setHotels]               = useState([]);
    const [hotelsLoading, setHotelsLoading] = useState(false);
    const [selectedHotel, setSelectedHotel] = useState(null);

    const [setupData, setSetupData]       = useState(null);
    const [setupLoading, setSetupLoading] = useState(false);

    const [selectedMarketplace, setSelectedMarketplace] = useState(null);
    const [udfField, setUdfField] = useState('UDFC01');

    const [startDate, setStartDate] = useState(DEFAULT_START);
    const [endDate, setEndDate]     = useState(DEFAULT_END);
    const [dateType, setDateType]   = useState('arrival');

    const [saving, setSaving] = useState(false);

    const cs = useCredentialStep({
        selectedMarketplace,
        selectedHotel,
        selectedAccount,
        selectedPartner,
        solePartnerId,
        isSuperAdmin,
        isPartner,
        isChainAdmin,
    });

    // Step 1 → Step 2 (Hotel)
    const goToStep2 = async () => {
        if (!form.name.trim()) { setErrors({ name: 'Name is required' }); return; }
        setErrors({});
        setHotelsLoading(true);
        try {
            const res = await hotelService.getAllHotels(
                selectedAccount ? { accountid: selectedAccount } : { partnerid: selectedPartner || solePartnerId }
            );
            setHotels(res.hotels || []);
        } catch (err) {
            console.error('Failed to load hotels', err);
        } finally {
            setHotelsLoading(false);
        }
        setStep(2);
    };

    // Step 2 → Step 3 (PMS)
    const goToStep3 = async () => {
        if (!selectedHotel) { setErrors({ hotel: 'Please select a hotel' }); return; }
        setErrors({});
        setSetupLoading(true);
        try {
            const credFetchers = [entityMktSettingsService.listCredentials('hotel', selectedHotel.hotelid)];
            if (selectedAccount) credFetchers.push(entityMktSettingsService.listCredentials('account', selectedAccount));
            const effectivePartnerId = selectedPartner || solePartnerId;
            if (effectivePartnerId) credFetchers.push(entityMktSettingsService.listCredentials('partner', effectivePartnerId));

            const credEntityTypes = ['hotel'];
            if (selectedAccount) credEntityTypes.push('account');
            if (effectivePartnerId) credEntityTypes.push('partner');

            const [setupRes, ...credResults] = await Promise.all([
                paymentporterService.getSetupData(selectedHotel.hotelid, selectedAccount),
                ...credFetchers,
            ]);
            setSetupData(setupRes.data);
            const merged = credResults.flatMap((r, i) =>
                (r.credentials || []).map(c => ({ ...c, _entityType: credEntityTypes[i] }))
            );
            cs.setCredentials(merged);
        } catch (err) {
            console.error('Failed to load setup data', err);
        } finally {
            setSetupLoading(false);
        }
        setStep(3);
    };

    // Step 3 → Step 4 (Credentials)
    const goToStep4 = async () => {
        if (!selectedMarketplace) { setErrors({ marketplace: 'Please select a marketplace' }); return; }
        setErrors({});
        await cs.loadForMarketplace();
        setStep(4);
    };

    // Step 4 → Step 5 (Marker)
    const goToStep5 = async () => {
        const err = cs.validate();
        if (err) { setErrors(err); return; }
        setErrors({});
        setStep(5);
    };

    // Step 5 → Step 6 (Dates)
    const goToStep6 = () => { setErrors({}); setStep(6); };

    const handleCreate = async () => {
        if (!startDate) { setErrors({ startDate: 'Start date is required' }); return; }
        if (!endDate)   { setErrors({ endDate: 'End date is required' }); return; }
        if (endDate < startDate) { setErrors({ endDate: 'End date must be on or after start date' }); return; }
        setSaving(true);
        setErrors({});
        try {
            const res = await pmmService.create({
                name:               form.name.trim(),
                description:        form.description.trim() || null,
                account_id:         selectedAccount,
                hotel_id:           selectedHotel?.hotelid || null,
                marketplace_id:     selectedMarketplace?.id || null,
                ...cs.buildCredentialPayload(),
                hotelid_override:   cs.hotelIdOverride.trim() || null,
                udf_field:          udfField,
                active:             true,
                default_start_date: startDate,
                default_end_date:   endDate,
                default_date_type:  dateType,
            });
            const newId = res.data?.id;
            navigate(newId ? `/apps/payment-migration-manager/${newId}` : '/apps/payment-migration-manager');
        } catch (err) {
            console.error('Failed to create PMM', err);
            setErrors({ submit: err.response?.data?.message || 'Failed to create Payment Migration Manager' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="ppw-page">
            <WizardStepper steps={STEPS} currentStep={step} />

            {/* ── Step 1: Name ─────────────────────────────────────────────── */}
            {step === 1 && (
                <div className="ppw-card">
                    <div className="ppw-card-header">
                        <h2 className="ppw-card-title">Name your Migration Manager</h2>
                        <p className="ppw-card-subtitle">Give this configuration a name</p>
                    </div>
                    <div className="ppw-card-body">
                        <div className="ppw-field">
                            <label className="ppw-label">Name <span className="ppw-required">*</span></label>
                            <input
                                className={`ppw-input${errors.name ? ' ppw-input-error' : ''}`}
                                type="text"
                                placeholder="e.g., Q1 Virtual Card Migration"
                                value={form.name}
                                onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setErrors(v => ({ ...v, name: undefined })); }}
                                autoFocus
                            />
                            {errors.name && <span className="ppw-error-msg">{errors.name}</span>}
                        </div>
                        <div className="ppw-field">
                            <label className="ppw-label">Description</label>
                            <textarea
                                className="ppw-input"
                                rows={3}
                                placeholder="Optional description"
                                value={form.description}
                                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                style={{ resize: 'vertical' }}
                            />
                        </div>
                    </div>
                    <div className="ppw-footer">
                        <button className="ppw-btn-cancel" onClick={() => navigate('/apps/payment-migration-manager')}>Cancel</button>
                        <button className="ppw-btn-next" onClick={goToStep2} disabled={hotelsLoading}>
                            {hotelsLoading ? 'Loading...' : 'Next →'}
                        </button>
                    </div>
                </div>
            )}

            {/* ── Step 2: Hotel ─────────────────────────────────────────────── */}
            {step === 2 && (
                <WizardHotelStep
                    hotels={hotels}
                    selectedHotel={selectedHotel}
                    onSelect={h => { setSelectedHotel(h); setErrors(v => ({ ...v, hotel: undefined })); }}
                    error={errors.hotel}
                    subtitle="Choose the hotel this migration is for"
                    loading={setupLoading}
                    onBack={() => setStep(1)}
                    onNext={goToStep3}
                />
            )}

            {/* ── Step 3: PMS ──────────────────────────────────────────────── */}
            {step === 3 && (
                <WizardPmsStep
                    marketplaces={setupData?.marketplaces || []}
                    selectedMarketplace={selectedMarketplace}
                    onSelect={m => { setSelectedMarketplace(m); setErrors(v => ({ ...v, marketplace: undefined })); }}
                    error={errors.marketplace}
                    subtitle="Choose the system this migration will connect to"
                    onBack={() => setStep(2)}
                    onNext={goToStep4}
                />
            )}

            {/* ── Step 4: Credentials ──────────────────────────────────────── */}
            {step === 4 && (
                <WizardCredentialsStep
                    selectedMarketplace={selectedMarketplace}
                    selectedHotel={selectedHotel}
                    accountId={selectedAccount}
                    partnerId={selectedPartner || solePartnerId}
                    cs={cs}
                    errors={errors}
                    onClearError={key => setErrors(v => ({ ...v, [key]: undefined }))}
                    onBack={() => { setStep(3); cs.setShowAddCredForm(false); }}
                    onNext={goToStep5}
                />
            )}

            {/* ── Step 5: Processed Marker (UDF) ───────────────────────────── */}
            {step === 5 && (
                <WizardMarkerStep
                    udfField={udfField}
                    onChange={setUdfField}
                    subtitle="OHIP user-defined character field where the migration records its marker so the same reservation isn't processed twice within a run."
                    onBack={() => setStep(4)}
                    onNext={goToStep6}
                />
            )}

            {/* ── Step 6: Date Range ────────────────────────────────────────── */}
            {step === 6 && (
                <div className="ppw-card">
                    <div className="ppw-card-header">
                        <h2 className="ppw-card-title">Default Date Range</h2>
                        <p className="ppw-card-subtitle">Set the default dates pre-filled when running this migration.</p>
                    </div>
                    <div className="ppw-card-body">
                        <div className="ppw-field">
                            <label className="ppw-label">Date Type</label>
                            <div className="ppw-entity-tabs">
                                <button type="button" className={`ppw-entity-tab${dateType === 'arrival' ? ' active' : ''}`} onClick={() => setDateType('arrival')}>
                                    Arrival
                                </button>
                                <button type="button" className={`ppw-entity-tab${dateType === 'departure' ? ' active' : ''}`} onClick={() => setDateType('departure')}>
                                    Departure
                                </button>
                            </div>
                            <div className="ppw-entity-hint">Reservations are selected by their {dateType} date falling within the range below.</div>
                        </div>

                        <div className="ppw-field">
                            <label className="ppw-label">Start Date <span className="ppw-required">*</span></label>
                            <input
                                className={`ppw-input${errors.startDate ? ' ppw-input-error' : ''}`}
                                type="date"
                                value={startDate}
                                onChange={e => { setStartDate(e.target.value); setErrors(v => ({ ...v, startDate: undefined, endDate: undefined })); }}
                                style={{ maxWidth: 220 }}
                            />
                            {errors.startDate && <span className="ppw-error-msg">{errors.startDate}</span>}
                        </div>

                        <div className="ppw-field">
                            <label className="ppw-label">End Date <span className="ppw-required">*</span></label>
                            <input
                                className={`ppw-input${errors.endDate ? ' ppw-input-error' : ''}`}
                                type="date"
                                value={endDate}
                                min={startDate}
                                onChange={e => { setEndDate(e.target.value); setErrors(v => ({ ...v, endDate: undefined })); }}
                                style={{ maxWidth: 220 }}
                            />
                            {errors.endDate && <span className="ppw-error-msg">{errors.endDate}</span>}
                        </div>

                        {errors.submit && <div className="ppw-error-msg" style={{ marginTop: '0.5rem' }}>{errors.submit}</div>}
                    </div>
                    <div className="ppw-footer">
                        <button className="ppw-btn-back" onClick={() => setStep(5)} disabled={saving}>← Back</button>
                        <button className="ppw-btn-next" onClick={handleCreate} disabled={saving}>
                            {saving ? 'Creating...' : 'Create Migration Manager'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddPaymentMigrationManagerWizard;
